/**
 * The tracker replay (Diurnal parity 03-031, migration plan T16, decision 75; docs/specs/tracker.md §5.8): play the
 * trails kept on this laptop back over the last 48 hours, with play, pause, speed and a scrub bar. The Diurnal tool had
 * no replay (inventory 03 §11: "History" only drew past trips); Google Earth's time slider on an exported trail KML was
 * the nearest thing. Teslyn replays on its own map, in every map window, from the same trails the map draws.
 *
 * - The range is the last 48 hours, from the oldest timed point kept (never older than 48 hours) to now.
 * - At a replay time every aircraft stands at its last genuine fix at or before that time (no position is invented
 *   between two fixes), with that fix's altitude, speed and heading; an aircraft not yet seen is not shown. Its trail
 *   is drawn up to that time over the map's trail depth, and its take-off, flight time and landing are worked out
 *   from the trail up to that time, as the live map does (`flightStatus`, 03-034).
 * - A replayed aircraft is airborne or on the ground by its speed; lost tracking and SOS are live alarms and are never
 *   raised again by a replay.
 * - A trail without times (a TracPlus path, issue #20) cannot be replayed; those aircraft are named.
 */
import type { AircraftPosition, TrailPoint } from './aircraft-layer.js';
import { flightStatus } from './flight-status.js';
import { formatOffset, utcToLocalMs } from './time-model.js';
import { AIRBORNE_KT, TRAIL_RETENTION_HOURS, type TrackPoint } from './tracker.js';

/** Replay reaches back as far as positions are kept (decision 75). */
export const REPLAY_HOURS = TRAIL_RETENTION_HOURS;
/** Replay speeds: replay seconds per real second. 300× plays a five-hour survey flight in a minute. */
export const REPLAY_SPEEDS = [10, 60, 300, 900, 3600] as const;
export type ReplaySpeed = (typeof REPLAY_SPEEDS)[number];
export const DEFAULT_REPLAY_SPEED: ReplaySpeed = 300;

export interface ReplayRange {
  readonly startMs: number;
  readonly endMs: number;
}

export interface ReplayClock {
  readonly range: ReplayRange;
  /** The replay time, UTC ms, inside the range. */
  readonly atMs: number;
  readonly playing: boolean;
  readonly speed: ReplaySpeed;
}

export type ReplayEvent =
  | { readonly type: 'play' }
  | { readonly type: 'pause' }
  | { readonly type: 'toggle' }
  | { readonly type: 'seek'; readonly atMs: number }
  | { readonly type: 'speed'; readonly speed: ReplaySpeed }
  /** Real time passed while playing, ms. */
  | { readonly type: 'tick'; readonly elapsedMs: number }
  /** The range moved on (now is later, or older points were pruned). */
  | { readonly type: 'range'; readonly range: ReplayRange };

const timed = (p: TrailPoint): p is TrailPoint & { timeMs: number } =>
  p.timeMs !== undefined && Number.isFinite(p.timeMs);

/**
 * The replay range: from the oldest timed point of any trail (at most 48 hours before now) to now; null when no trail
 * has a timed point in the last 48 hours.
 */
export function replayRange(
  trails: ReadonlyMap<string, readonly TrailPoint[]>,
  nowMs: number,
): ReplayRange | null {
  const floor = nowMs - REPLAY_HOURS * 3_600_000;
  let first = Infinity;
  for (const t of trails.values())
    for (const p of t)
      if (timed(p) && p.timeMs >= floor && p.timeMs <= nowMs && p.timeMs < first) first = p.timeMs;
  return Number.isFinite(first) ? { startMs: first, endMs: nowMs } : null;
}

const clamp = (v: number, r: ReplayRange): number => Math.min(Math.max(v, r.startMs), r.endMs);

/** A replay at the start of its range, paused, at the default speed. */
export function startReplay(range: ReplayRange, speed: ReplaySpeed = DEFAULT_REPLAY_SPEED): ReplayClock {
  return { range, atMs: range.startMs, playing: false, speed };
}

export function replayReducer(c: ReplayClock, e: ReplayEvent): ReplayClock {
  switch (e.type) {
    case 'play':
      // play at the end starts again from the beginning
      return { ...c, playing: true, atMs: c.atMs >= c.range.endMs ? c.range.startMs : c.atMs };
    case 'pause':
      return { ...c, playing: false };
    case 'toggle':
      return replayReducer(c, { type: c.playing ? 'pause' : 'play' });
    case 'seek':
      return Number.isFinite(e.atMs) ? { ...c, atMs: clamp(e.atMs, c.range) } : c;
    case 'speed':
      return REPLAY_SPEEDS.includes(e.speed) ? { ...c, speed: e.speed } : c;
    case 'tick': {
      if (!c.playing || !(e.elapsedMs > 0)) return c;
      const at = c.atMs + e.elapsedMs * c.speed;
      return at >= c.range.endMs ? { ...c, atMs: c.range.endMs, playing: false } : { ...c, atMs: at };
    }
    case 'range':
      return { ...c, range: e.range, atMs: clamp(c.atMs, e.range) };
  }
}

/** Where the replay time is along its range, 0 to 1 (the scrub bar). */
export function replayFraction(c: ReplayClock): number {
  const span = c.range.endMs - c.range.startMs;
  return span > 0 ? (c.atMs - c.range.startMs) / span : 1;
}

const upTo = (trail: readonly TrailPoint[], atMs: number, fromMs: number): TrackPoint[] =>
  trail
    .filter(timed)
    .filter((p) => p.timeMs <= atMs && p.timeMs >= fromMs)
    .sort((a, b) => a.timeMs - b.timeMs)
    .map((p) => ({
      lat: p.lat,
      lon: p.lon,
      timeMs: p.timeMs,
      altFt: p.altFt ?? null,
      speedKt: p.speedKt ?? null,
      headingDeg: p.headingDeg ?? null,
      event: p.event ?? null,
    }));

/**
 * Every trail as it stood at the replay time, over the map's trail depth (hours, at most 48): timed points from
 * `atMs − depth` to `atMs`, in time order. Untimed points (a TracPlus path) are left out: they have no place in time.
 */
export function replayTrails(
  trails: ReadonlyMap<string, readonly TrailPoint[]>,
  atMs: number,
  depthHours: number,
): Map<string, TrailPoint[]> {
  const from = atMs - Math.min(Math.max(depthHours, 0), REPLAY_HOURS) * 3_600_000;
  const out = new Map<string, TrailPoint[]>();
  for (const [id, t] of trails) {
    const cut = upTo(t, atMs, from);
    if (cut.length > 0) out.set(id, cut);
  }
  return out;
}

/**
 * The aircraft as they stood at the replay time: each at its last timed fix at or before `atMs` (and not older than
 * 48 hours before it), with that fix's altitude, speed and heading; airborne by its speed (40 knots fixed-wing, 20
 * helicopter); the organisation's aircraft with their take-off, flight time and landing up to then. An aircraft with
 * no fix by then is left out. Lost tracking and SOS are not replayed.
 */
export function replayAircraft(
  aircraft: readonly AircraftPosition[],
  trails: ReadonlyMap<string, readonly TrailPoint[]>,
  atMs: number,
): AircraftPosition[] {
  const from = atMs - REPLAY_HOURS * 3_600_000;
  const out: AircraftPosition[] = [];
  for (const a of aircraft) {
    const pts = upTo(trails.get(a.id) ?? [], atMs, from);
    const fix = pts[pts.length - 1];
    if (!fix) continue;
    const airborne = fix.speedKt !== null && fix.speedKt >= AIRBORNE_KT[a.kind];
    const flight = a.fleet ? flightStatus(pts, a.kind, atMs) : null;
    const rest: AircraftPosition = { ...a };
    // the live flight line belongs to now, not to the replay time
    delete (rest as { flight?: unknown }).flight;
    out.push({
      ...rest,
      lat: fix.lat,
      lng: fix.lon,
      alt_ft: fix.altFt,
      speed_kt: fix.speedKt,
      heading_deg: fix.headingDeg,
      time: new Date(fix.timeMs).toISOString(),
      airborne,
      ...(a.state !== undefined ? { state: airborne ? ('airborne' as const) : ('parked' as const) } : {}),
      ...(flight ? { flight } : {}),
    });
  }
  return out;
}

/** The aircraft on the map whose trail has no times (a TracPlus path), which a replay cannot show. */
export function replayUntimed(
  aircraft: readonly AircraftPosition[],
  trails: ReadonlyMap<string, readonly TrailPoint[]>,
): string[] {
  return aircraft
    .filter((a) => {
      const t = trails.get(a.id) ?? [];
      return t.length > 0 && !t.some(timed);
    })
    .map((a) => a.label);
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const two = (n: number): string => String(n).padStart(2, '0');
const hms = (d: Date): string =>
  `${two(d.getUTCHours())}:${two(d.getUTCMinutes())}:${two(d.getUTCSeconds())}`;

/** The replay time in words, in the project's time zone and in UTC: "Sun 19 Oct, 14:22:00 (UTC+8), 06:22:00 UTC". */
export function replayTimeWords(atMs: number, offsetH: number): string {
  const l = new Date(utcToLocalMs(atMs, offsetH));
  return `${DAYS[l.getUTCDay()]} ${l.getUTCDate()} ${MONTHS[l.getUTCMonth()]}, ${hms(l)} (UTC${formatOffset(offsetH)}), ${hms(new Date(atMs))} UTC`;
}

/** A speed in words for its menu: "10×", "1 min a second", "5 min a second", "15 min a second", "1 h a second". */
export function replaySpeedWords(s: ReplaySpeed): string {
  if (s < 60) return `${s}× (${s} seconds a second)`;
  if (s < 3600) return `${s}× (${s / 60} min a second)`;
  return `${s}× (${s / 3600} h a second)`;
}
